import { PickerValue, ScrollPicker } from "@/components/scroll-picker";
import { QueryKeys } from "@/constants/query-keys";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { useQueryClient } from "@tanstack/react-query";
import { subYears } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

const MONTH_LABELS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const pad = (n: number) => String(n).padStart(2, "0");

const buildYearOptions = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const buildDayOptions = (year: number, month: number) =>
  Array.from({ length: new Date(year, month, 0).getDate() }, (_, i) => pad(i + 1));

const MONTH_OPTIONS = MONTH_LABELS.map((_, i) => pad(i + 1));

const renderDarkItem = (value: string, selected: boolean, display?: string) => (
  <div
    className={cn(
      "flex h-[52px] w-full items-center justify-center text-[22px] font-semibold",
      selected ? "text-white" : "text-[#b1bbd1]",
    )}
  >
    {display ?? value}
  </div>
);

const BirthdayPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { user } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const personalInfo = user?.personalInfo;
  const hydratedRef = useRef(false);

  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => subYears(today, 13), [today]);
  const minYear = 1900;
  const maxYear = maxDate.getFullYear();

  const initialDate = useMemo(() => {
    if (personalInfo?.dob) {
      const parsed = new Date(personalInfo.dob);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date(1995, 0, 1);
  }, [personalInfo?.dob]);

  const [pickerValue, setPickerValue] = useState<PickerValue>({
    day: pad(initialDate.getDate()),
    month: pad(initialDate.getMonth() + 1),
    year: String(initialDate.getFullYear()),
  });

  useEffect(() => {
    if (hydratedRef.current) return;
    if (personalInfo?.dob) {
      const parsed = new Date(personalInfo.dob);
      if (!isNaN(parsed.getTime())) {
        setPickerValue({
          day: pad(parsed.getDate()),
          month: pad(parsed.getMonth() + 1),
          year: String(parsed.getFullYear()),
        });
        hydratedRef.current = true;
      }
    }
  }, [personalInfo?.dob]);

  const yearOptions = useMemo(() => buildYearOptions(minYear, maxYear), [minYear, maxYear]);

  const dayOptions = useMemo(
    () => buildDayOptions(Number(pickerValue.year), Number(pickerValue.month)),
    [pickerValue.year, pickerValue.month],
  );

  const handleSubmit = async () => {
    if (isUpdating) return;
    triggerHaptic();
    const dob = `${pickerValue.year}-${pickerValue.month}-${pickerValue.day}`;
    try {
      await Promise.all([
        preloadNextRoute(),
        updateUserAsync({
          firstName: personalInfo?.firstName ?? "",
          lastName: personalInfo?.lastName ?? "",
          dob,
          sex: personalInfo?.sex ?? "",
        }),
      ]);
      queryClient.setQueryData([QueryKeys.USER], (old: object) => ({
        ...old,
        personalInfo: { ...personalInfo, dob },
      }));
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What’s your birth date?"
      subtitle="This helps me calculate things more accurately"
      footer={<OnboardingPillButton text="Continue" onClick={handleSubmit} disabled={isUpdating} />}
    >
      <div className="pt-2">
        <ScrollPicker
          value={pickerValue}
          onChange={setPickerValue}
          height={156}
          itemHeight={52}
          highlightColor="rgba(0,0,0,0.28)"
          style={{ width: "100%", gap: "8px" }}
        >
          <ScrollPicker.Column
            name="day"
            options={dayOptions}
            renderItem={(v, s) => renderDarkItem(v, s)}
          />
          <ScrollPicker.Column
            name="month"
            options={MONTH_OPTIONS}
            renderItem={(v, s) => renderDarkItem(v, s, MONTH_LABELS[Number(v) - 1])}
          />
          <ScrollPicker.Column
            name="year"
            options={yearOptions}
            renderItem={(v, s) => renderDarkItem(v, s)}
          />
        </ScrollPicker>
      </div>
    </OnboardingStepShellV5>
  );
};

export default BirthdayPage;
